import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // Find the row that contains this line
    const lineItemRow = this.#findRowByLineIndex(line);
    if (!lineItemRow) return;

    // Check if this is a bundled item
    const bundledIndices = lineItemRow.dataset.bundledIndices;
    const totalQuantity = parseInt(lineItemRow.dataset.totalQuantity || '1', 10);

    if (quantity === 0) {
      // If bundled, we need to remove all bundled items
      if (bundledIndices && bundledIndices.includes(',')) {
        this.#removeBundledItems(bundledIndices, lineItemRow);
      } else {
        return this.onLineItemRemove(line);
      }
    } else {
      // For bundled items, we need to update all related items proportionally
      if (bundledIndices && bundledIndices.includes(',')) {
        this.#updateBundledItems(bundledIndices, quantity, totalQuantity, lineItemRow);
      } else {
        this.updateQuantity({
          line,
          quantity,
          action: 'change',
        });
      }
    }

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Find row by line index, checking bundled items.
   * @param {number} line - The line item index.
   * @returns {HTMLTableRowElement | null}
   */
  #findRowByLineIndex(line) {
    // Check all rows since bundled items might not be in order
    for (const row of this.refs.cartItemRows) {
      const firstLine = parseInt(row.dataset.firstLine, 10);
      if (firstLine === line) {
        return row;
      }
      
      // Check if line is part of a bundled item
      const bundledIndices = row.dataset.bundledIndices;
      if (bundledIndices) {
        const indices = bundledIndices.split(',').map(idx => parseInt(idx, 10) + 1);
        if (indices.includes(line)) {
          return row;
        }
      }
    }

    return null;
  }

  /**
   * Remove all bundled items.
   * @param {string} bundledIndices - Comma-separated list of 0-based indices.
   * @param {HTMLTableRowElement} row - The row element.
   */
  async #removeBundledItems(bundledIndices, row) {
    // Check if we have bundled keys in the dataset
    const bundledKeys = row.dataset.bundledKeys;
    
    console.log('Removing bundled items:', { bundledIndices, bundledKeys });
    
    if (bundledKeys) {
      // Use the stored keys directly
      const itemKeys = bundledKeys.split(',').map(key => key.trim());
      
      // Filter out any invalid keys
      const validKeys = itemKeys.filter(key => {
        // Shopify cart keys are usually in format "variantId:hash" or just "hash"
        const isValid = key.length > 10 && !key.includes(' ') && isNaN(parseFloat(key));
        if (!isValid) {
          console.warn('Invalid cart key found:', key);
        }
        return isValid;
      });
      
      console.log('Valid keys to remove:', validKeys);
      
      if (validKeys.length === 0) {
        console.error('No valid keys found, falling back to index-based removal');
        return this.#removeBundledItemsByIndices(bundledIndices, row);
      }
      
      const updates = validKeys.map(key => ({
        id: key,
        quantity: 0
      }));

      console.log('Updates to send:', updates);
      this.#updateMultipleItems(updates, 'clear', row);
    } else {
      this.#removeBundledItemsByIndices(bundledIndices, row);
    }
  }
  
  /**
   * Remove bundled items by fetching cart and using indices.
   * @param {string} bundledIndices - Comma-separated list of 0-based indices.
   * @param {HTMLTableRowElement} row - The row element.
   */
  async #removeBundledItemsByIndices(bundledIndices, row) {
    const lineNumbers = bundledIndices.split(',').map(idx => parseInt(idx, 10) + 1);
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      console.log('Current cart items:', cart.items.map((item, idx) => ({
        index: idx,
        key: item.key,
        product: item.product_title,
        quantity: item.quantity
      })));
      
      const updates = lineNumbers.map(lineNumber => {
        const item = cart.items[lineNumber - 1];
        if (item) {
          return {
            id: item.key,
            quantity: 0
          };
        }
        return null;
      }).filter(Boolean);

      console.log('Fallback updates to send:', updates);
      if (updates.length > 0) {
        this.#updateMultipleItems(updates, 'clear', row);
      }
    } catch (error) {
      console.error('Failed to get cart items:', error);
      // Last resort: try removing items one by one
      for (const lineNumber of lineNumbers) {
        await this.updateQuantity({
          line: lineNumber,
          quantity: 0,
          action: 'clear',
        });
      }
    }
  }

  /**
   * Update bundled items proportionally.
   * @param {string} bundledIndices - Comma-separated list of 0-based indices.
   * @param {number} newQuantity - The new total quantity.
   * @param {number} currentTotalQuantity - The current total quantity.
   * @param {HTMLTableRowElement} row - The row element.
   */
  async #updateBundledItems(bundledIndices, newQuantity, currentTotalQuantity, row) {
    const bundledKeys = row.dataset.bundledKeys;
    
    if (bundledKeys) {
      // Use stored keys and distribute quantity evenly
      const itemKeys = bundledKeys.split(',');
      const quantityPerItem = Math.floor(newQuantity / itemKeys.length);
      const remainder = newQuantity % itemKeys.length;

      const updates = itemKeys.map((key, index) => ({
        id: key,
        quantity: quantityPerItem + (index < remainder ? 1 : 0)
      }));

      this.#updateMultipleItems(updates, 'change', row);
    } else {
      // Fallback: fetch cart to get current quantities and keys
      const lineNumbers = bundledIndices.split(',').map(idx => parseInt(idx, 10) + 1);
      
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Calculate the quantity change ratio
        const ratio = newQuantity / currentTotalQuantity;
        
        // Create updates array with proportional quantities
        const updates = lineNumbers.map(lineNumber => {
          const item = cart.items[lineNumber - 1];
          if (item) {
            const newItemQuantity = Math.round(item.quantity * ratio);
            return {
              id: item.key,
              quantity: newItemQuantity
            };
          }
          return null;
        }).filter(Boolean);

        // Adjust for rounding errors to ensure total matches newQuantity
        const totalAssigned = updates.reduce((sum, update) => sum + update.quantity, 0);
        if (totalAssigned !== newQuantity && updates.length > 0) {
          updates[0].quantity += (newQuantity - totalAssigned);
        }

        if (updates.length > 0) {
          this.#updateMultipleItems(updates, 'change', row);
        }
      } catch (error) {
        console.error('Failed to get cart items:', error);
        // Fallback to updating first item only
        this.updateQuantity({
          line: lineNumbers[0],
          quantity: newQuantity,
          action: 'change',
        });
      }
    }
  }

  /**
   * Update multiple cart items at once.
   * @param {Array<{id: string, quantity: number}>} updates - Array of updates.
   * @param {string} action - The action type.
   * @param {HTMLTableRowElement} row - The row element.
   */
  #updateMultipleItems(updates, action, row) {
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Convert array of updates to object format expected by Shopify
    const updatesObject = {};
    updates.forEach(update => {
      updatesObject[update.id] = update.quantity;
    });

    const body = JSON.stringify({
      updates: updatesObject,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    console.log('Sending cart update:', { updates: updatesObject, body });

    cartTotal?.shimmer();

    // Use the /cart/update.js endpoint for multiple items
    fetch(`${Theme.routes.cart_update_url || '/cart/update.js'}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        console.log('Cart update response:', parsedResponseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Cart update errors:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        console.log('New cart item count:', newCartItemCount);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (action === 'clear' && row) {
          const remove = () => {
            row.remove();
            // After removing the row, morph the section to get the updated state
            morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          };
          if (prefersReducedMotion()) {
            remove();
          } else {
            row.style.setProperty('--row-height', `${row.clientHeight}px`);
            row.classList.add('removing');
            onAnimationEnd(row, remove);
          }
        } else {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
      });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const lineItemRow = this.#findRowByLineIndex(line);
    if (!lineItemRow) return;

    // Check if this is a bundled item
    const bundledIndices = lineItemRow.dataset.bundledIndices;
    const bundledKeys = lineItemRow.dataset.bundledKeys;
    
    if (bundledKeys && bundledKeys.includes(',')) {
      // This is a bundled item with multiple keys
      this.#removeBundledItems(bundledIndices, lineItemRow);
    } else {
      // Single item removal
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });

      const remove = () => lineItemRow.remove();

      if (prefersReducedMotion()) return remove();

      // Add class to the row to trigger the animation
      lineItemRow.style.setProperty('--row-height', `${lineItemRow.clientHeight}px`);
      lineItemRow.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(lineItemRow, remove);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}